import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'
import { VariantProps, cva } from 'class-variance-authority'
import { ParagraphText } from '../paragraph-text'
import { Heading } from '../index'

const cardVariants = cva(
  'col-span-4 lg:col-span-3 items-center justify-center bg-surface-high text-sm font-medium rounded-lg',
  {
    variants: {
      variant: {
        project: 'text-on-surface shadow-md-custom p-sm'
      }
    },
    defaultVariants: {
      variant: 'project'
    }
  }
)

interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {
  asChild?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div'
  return <Comp ref={ref} className={cn(cardVariants({ variant, className }))} {...props} />
})
Card.displayName = 'Card'

const FloatingCardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('space-y-xs col-span-4 flex flex-col lg:col-span-1', className)} {...props} />
  )
)
FloatingCardHeader.displayName = 'FloatingCardHeader'

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => <Heading size="lg" weight="medium" ref={ref} className={className} {...props} />
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => <ParagraphText ref={ref} className={'text-on-surface-variant'} {...props} />
)
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('gap-y-lg gap-x-md grid', className)} {...props} />
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <Heading size="lg" ref={ref} className={className} {...props} />
)
CardFooter.displayName = 'CardFooter'

export { Card, FloatingCardHeader, CardFooter, CardTitle, CardDescription, CardContent }
